import React, { useContext } from 'react';
import { Flex } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import MessageActionTable from '@totem/components/ticketing/details/messages/messageActionTable';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import { TicketMessage } from '@totem/components/ticketing/ticketing';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { formatDate, isNotNull } from '@totem/utilities/common';

const MessagesTable = () => {
  const { ticket } = useContext(TicketContext);

  const getReferenceItem = (message: TicketMessage) => {
    switch (message.objectReference.name) {
      case 'Ticket':
        return (
          <Flex justify={'space-between'} align={'center'}>
            <div>{ticket.ticket.number}</div>
            <div>{formatDate(ticket.ticket.createdAt)}</div>
          </Flex>
        );
      case 'Activity': {
        if (isNotNull(ticket.ticket.activity)) {
          const activity = ticket.ticket.activity.find(
            (chk) => chk.id === message.objectReference.id,
          );
          if (isNotNull(activity)) {
            return (
              <Flex justify={'space-between'} align={'center'}>
                <div>{activity.type.display}</div>
                <div>{formatDate(activity.createdAt)}</div>
              </Flex>
            );
          }
        }
        return <span />;
      }
      case 'Party Involved': {
        if (isNotNull(ticket.ticket.partiesInvolved)) {
          const partyInvolved = ticket.ticket.partiesInvolved.find(
            (chk) => chk.id === message.objectReference.id,
          );
          if (isNotNull(partyInvolved)) {
            return (
              <Flex justify={'space-between'} align={'center'}>
                <div>{partyInvolved.type}</div>
                <div>{formatDate(partyInvolved.involvementStart)}</div>
              </Flex>
            );
          }
        }
        return <span />;
      }
      case 'Queue Assignment': {
        if (isNotNull(ticket.ticket.queueAssignments)) {
          const queueAssignment = ticket.ticket.queueAssignments.find(
            (chk) => chk.id === message.objectReference.id,
          );
          if (isNotNull(queueAssignment)) {
            return (
              <Flex justify={'space-between'} align={'center'}>
                <div>{queueAssignment.name}</div>
                <div>{formatDate(queueAssignment.startTime)}</div>
              </Flex>
            );
          }
        }
        return <span />;
      }
      default:
        return <span />;
    }
  };

  const columns: ColumnProps<TicketMessage>[] = [
    {
      title: 'Initiated From',
      dataIndex: 'objectReference.name',
      key: 'objectReference.name',
      render: (_, record: TicketMessage) => record.objectReference.name,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        typeof compA.objectReference.name === compB.objectReference.name
          ? 0
          : compA.objectReference.name > compB.objectReference.name
            ? 1
            : -1,
    },
    {
      title: 'Reference',
      dataIndex: 'objectReference',
      key: 'objectReference',
      render: (_, record: TicketMessage) => getReferenceItem(record),
      showSorterTooltip: false,
    },
    {
      title: 'Recipients',
      dataIndex: 'recipients',
      key: 'recipients',
      render: (_, record: TicketMessage) =>
        record.recipients.map((item) => (
          <div key={item.address}>{item.address}</div>
        )),
      showSorterTooltip: false,
    },
  ];

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={
        isNotNull(ticket) && isNotNull(ticket.ticket.messages)
          ? ticket.ticket.messages
          : []
      }
      loading={false}
      rowKey={(record) =>
        record.internalMessageID ||
        record.xMessageID ||
        `${record.objectReference?.id || 'unknown'}_${record.templateId}`
      }
      expandable={{
        expandedRowRender: (record) => (
          <MessageActionTable messageActivity={record.activities} />
        ),
        defaultExpandAllRows: false,
      }}
    />
  );
};

export default MessagesTable;
